import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Modal, Typography, message, Space, Input, Card, App, AutoComplete, Tag, Switch } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../utils/api';
import type { ColumnsType } from 'antd/es/table';
import { DatabaseOutlined, DropboxOutlined, CheckCircleOutlined, SearchOutlined, CloseCircleOutlined, PlusOutlined, UpOutlined, DownOutlined, WarningOutlined, CalendarOutlined, EnvironmentOutlined, AppstoreOutlined, EditOutlined, FilePdfOutlined } from '@ant-design/icons';
import { theme } from '../../theme';
import { StandardCard, ExportExcelButton, ExportPDFButton, ColumnManagerDropdown } from '../common';
import { useColumnManager } from '../../hooks';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

interface SiloBolsa {
  id: number;
  numero_silobolsa: number;
  kilos: number;
  ubicacion: string;
  observacion: string;
  fecha_alta: string;
  fecha_cierre: string;
  estado: string;
  producto: string;
}

interface ListadoSilobolsaResponse {
  silos_bolsas: SiloBolsa[];
  total: number;
}

const { Title, Text } = Typography;

const ListadoSilobolsa: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const { message: messageApi } = App.useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [silosBolsas, setSilosBolsas] = useState<SiloBolsa[]>([]);
  const [searchUbicacion, setSearchUbicacion] = useState<string>('');
  const [globalSearch, setGlobalSearch] = useState<string>('');
  const [showStatsExpanded, setShowStatsExpanded] = useState(false);

  // Lista de todas las columnas disponibles (keys)
  const allColumnKeys = [
    'numero_silobolsa',
    'kilos',
    'ubicacion',
    'observacion',
    'fecha_alta',
    'fecha_cierre',
    'estado',
    'acciones'
  ];

  // Nombres amigables para las columnas
  const columnNames: Record<string, string> = {
    'numero_silobolsa': 'Número Silo',
    'kilos': 'Kilos',
    'ubicacion': 'Ubicación',
    'observacion': 'Observación',
    'fecha_alta': 'Fecha Alta',
    'fecha_cierre': 'Fecha Cierre',
    'estado': 'Estado',
    'acciones': 'Acciones'
  };

  const id_empresa = user?.id_empresa;
  const id_sucursal = user?.id_sucursal;

  useEffect(() => {
    cargarSilosBolsas();
  }, [id_empresa, id_sucursal]);

  // Visibilidad + orden de columnas (persistido) — reemplaza el manejo manual de antes.
  const columnManager = useColumnManager({
    storageKey: 'silobolsa',
    allKeys: allColumnKeys,
    requiredKeys: ['acciones'],
  });

  const cargarSilosBolsas = async () => {
    if (!id_empresa || !id_sucursal) {
      messageApi.error('No se encontraron datos de empresa/sucursal');
      return;
    }

    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const caso = "B";
      const response = await apiClient.get<ListadoSilobolsaResponse>(
        `${process.env.REACT_APP_SILOBOLSA_URL}/listado_silobolsa/${id_empresa}/${id_sucursal}/${caso}/`,
        { token }
      );

      setSilosBolsas(response.data.silos_bolsas);
    } catch (error: any) {
      console.error('Error al cargar silos bolsas:', error);
      messageApi.error(error.response?.data?.error || 'Error al cargar el listado de silos bolsas');
    } finally {
      setLoading(false);
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'Activo':
        return 'green';
      case 'Baja':
        return 'red';
      case 'Cerrado':
        return 'orange';
      default:
        return 'default';
    }
  };

  const handleEditar = (id: number) => {
    navigate(`/editar_silobolsa/${id}`);
  };

  const handleCierre = (id: number) => {
    Modal.confirm({
      title: 'Confirmar Cierre',
      content: '¿Está seguro de que desea cerrar este silo bolsa? Esta acción no se puede deshacer.',
      okText: 'Sí, Cerrar',
      cancelText: 'Cancelar',
      okType: 'danger',
      onOk: async () => {
        await cerrarSilobolsa(id);
      },
    });
  };

  const handleEmision = (id: number) => {
    navigate(`/emision_silobolsa/${id}`);
  };

  const cerrarSilobolsa = async (id: number) => {
    if (!id_empresa || !id_sucursal) {
      messageApi.error('No se encontraron datos necesarios');
      return;
    }

    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      await apiClient.post(
        `${process.env.REACT_APP_SILOBOLSA_URL}/cerrar_silobolsa/${id_empresa}/${id_sucursal}/${id}/`,
        {},
        { token }
      );

      messageApi.success('Silo bolsa cerrado exitosamente');
      cargarSilosBolsas(); // Recargar la lista
    } catch (error: unknown) {
      console.error('Error al cerrar silo bolsa:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Error al cerrar el silo bolsa';
      messageApi.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<SiloBolsa> = [
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Número Silo</span>,
      dataIndex: 'numero_silobolsa',
      key: 'numero_silobolsa',
      width: 120,
      align: 'center' as const,
      sorter: (a, b) => a.numero_silobolsa - b.numero_silobolsa,
      render: (value) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{value}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Kilos</span>,
      dataIndex: 'kilos',
      key: 'kilos',
      width: 120,
      align: 'right' as const,
      render: (kilos) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
          {kilos.toLocaleString('es-AR')}
        </span>
      ),
      sorter: (a, b) => a.kilos - b.kilos,
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Ubicación</span>,
      dataIndex: 'ubicacion',
      key: 'ubicacion',
      width: 200,
      ellipsis: true,
      sorter: (a, b) => (a.ubicacion || '').localeCompare(b.ubicacion || ''),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        const handleSearch = () => {
          confirm();
        };

        const handleReset = () => {
          if (clearFilters) {
            clearFilters();
          }
          setSearchUbicacion('');
          confirm();
        };

        return (
          <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
            <Input
              placeholder="Buscar ubicación..."
              value={searchUbicacion}
              onChange={(e) => {
                setSearchUbicacion(e.target.value);
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={handleSearch}
              style={{ 
                display: 'block',
                width: 188,
                marginBottom: 8
              }}
            />
            <Space>
              <Button
                type="primary"
                onClick={handleSearch}
                icon={<SearchOutlined />}
                size="small"
                style={{
                  width: 90,
                  backgroundColor: theme.colors.primary,
                  borderColor: theme.colors.primary,
                  borderRadius: theme.borderRadius.full
                }}
              >
                Buscar
              </Button>
              <Button
                onClick={handleReset}
                size="small"
                style={{ width: 90 }}
              >
                Limpiar
              </Button>
            </Space>
          </div>
        );
      },
      onFilter: (value, record) =>
        record.ubicacion?.toLowerCase().includes((value as string).toLowerCase()) || false,
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? theme.colors.secondary : theme.colors.textSecondary }} />
      ),
      render: (text) => (
        <span style={{ color: theme.colors.textSecondary }}>{text}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Observación</span>,
      dataIndex: 'observacion',
      key: 'observacion',
      width: 200,
      ellipsis: true,
      render: (observacion) => (
        <span style={{ color: theme.colors.textSecondary }}>{observacion || '-'}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Fecha Alta</span>,
      dataIndex: 'fecha_alta',
      key: 'fecha_alta',
      width: 120,
      align: 'center' as const,
      sorter: (a, b) => new Date(a.fecha_alta).getTime() - new Date(b.fecha_alta).getTime(),
      render: (text) => (
        <span style={{ color: theme.colors.textSecondary }}>{text}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Fecha Cierre</span>,
      dataIndex: 'fecha_cierre',
      key: 'fecha_cierre',
      width: 120,
      align: 'center' as const,
      sorter: (a, b) => new Date(a.fecha_cierre).getTime() - new Date(b.fecha_cierre).getTime(),
      render: (text) => (
        <span style={{ color: theme.colors.textSecondary }}>{text || '-'}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Estado</span>,
      dataIndex: 'estado',
      key: 'estado',
      width: 100,
      align: 'center' as const,
      render: (estado) => {
        const isActivo = estado === 'Activo';
        const isCerrado = estado === 'Cerrado';
        const backgroundColor = isActivo ? theme.colors.successLight : isCerrado ? theme.colors.warningLight : theme.colors.errorLight;
        const color = isActivo ? theme.colors.successDark : isCerrado ? theme.colors.warningDark : theme.colors.errorDark;
        
        return (
          <span style={{ 
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: '9999px',
            fontSize: '12px',
            fontWeight: 600,
            backgroundColor,
            color
          }}>
            {estado}
          </span>
        );
      },
      filters: [
        { text: 'Activo', value: 'Activo' },
        { text: 'Baja', value: 'Baja' },
        { text: 'Cerrado', value: 'Cerrado' },
      ],
      onFilter: (value, record) => record.estado === value,
      filterSearch: true,
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Acciones</span>,
      key: 'acciones',
      align: 'center' as const,
      width: 220,
      fixed: 'right' as const,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-start' }}>
          {(record.estado === 'Activo' || record.estado === 'Cerrado') && (
            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={() => handleEmision(record.id)}
              size="small"
              style={{
                backgroundColor: theme.colors.success,
                borderColor: theme.colors.success
              }}
              title="Emisión"
            >
              Emisión
            </Button>
          )}
          {record.estado !== 'Cerrado' && (
            <>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => handleEditar(record.id)}
                size="small"
                title="Editar registro"
              />
              <button
                onClick={() => handleCierre(record.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: theme.colors.error,
                  fontSize: '18px',
                  padding: '4px 8px',
                  display: 'flex',
                  alignItems: 'center'
                }}
                title="Cerrar silo bolsa"
                onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.errorDark}
                onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.error}
              >
                {React.createElement(CloseCircleOutlined as any)}
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  // Columnas visibles, en el orden elegido por el usuario en el selector de columnas
  const filteredColumns = useMemo(() => {
    const columnsByKey = new Map(columns.map(col => [col.key as string, col]));
    return columnManager.orderedVisibleKeys
      .map(key => columnsByKey.get(key))
      .filter((col): col is (typeof columns)[number] => Boolean(col));
  }, [columnManager.orderedVisibleKeys, columns]);

  const handleNuevo = () => {
    navigate('/crear_silobolsa');
  };

  // Función para resaltar términos en el texto
  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm.trim() || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} style={{
          backgroundColor: theme.colors.warningLight,
          padding: '2px 4px',
          borderRadius: '4px',
          fontWeight: 600
        }}>
          {part}
        </mark>
      ) : part
    );
  };

  // Filtrar datos según búsqueda global
  const filteredData = useMemo(() => {
    if (!globalSearch.trim()) {
      return silosBolsas;
    }

    const searchLower = globalSearch.toLowerCase();
    return silosBolsas.filter(record => {
      return (
        record.numero_silobolsa.toString().includes(searchLower) ||
        record.kilos.toString().includes(searchLower) ||
        (record.ubicacion || '').toLowerCase().includes(searchLower) ||
        (record.observacion || '').toLowerCase().includes(searchLower) ||
        record.fecha_alta.toLowerCase().includes(searchLower) ||
        (record.fecha_cierre || '').toLowerCase().includes(searchLower) ||
        record.estado.toLowerCase().includes(searchLower)
      );
    });
  }, [silosBolsas, globalSearch]);

  // Calcular estadísticas mejoradas
  const estadisticas = useMemo(() => {
    const activos = filteredData.filter(s => s.estado === 'Activo').length;
    const cerrados = filteredData.filter(s => s.estado === 'Cerrado').length;
    const baja = filteredData.filter(s => s.estado === 'Baja').length;
    const kilosTotal = filteredData.reduce((acc, item) => acc + (item.kilos || 0), 0);
    const promedioKilos = filteredData.length > 0 ? Math.round(kilosTotal / filteredData.length) : 0;
    
    // Top 3 ubicaciones
    const ubicacionesCount = filteredData.reduce((acc, record) => {
      const ubic = record.ubicacion || 'Sin ubicación';
      acc[ubic] = (acc[ubic] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const topUbicaciones = Object.entries(ubicacionesCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([nombre, count]) => ({ nombre, count }));

    // Último silo agregado (más reciente por fecha_alta)
    const ultimoSilo = filteredData.length > 0 
      ? filteredData.reduce((max, record) => {
          const maxDate = new Date(max.fecha_alta);
          const recordDate = new Date(record.fecha_alta);
          return recordDate > maxDate ? record : max;
        }, filteredData[0])
      : null;

    // Silos activos más antiguos (sin cerrar por más tiempo)
    const silosActivosAntiguos = filteredData
      .filter(s => s.estado === 'Activo')
      .sort((a, b) => new Date(a.fecha_alta).getTime() - new Date(b.fecha_alta).getTime())
      .slice(0, 3);

    return {
      total: filteredData.length,
      activos,
      cerrados,
      baja,
      kilosTotal,
      promedioKilos,
      topUbicaciones,
      ultimoSilo,
      silosActivosAntiguos
    };
  }, [filteredData]);

  // Función para exportar a Excel
  const handleExportExcel = () => {
    try {
      const worksheet = XLSX.utils.json_to_sheet(
        filteredData.map(record => ({
          'Número Silo': record.numero_silobolsa,
          'Kilos': record.kilos,
          'Ubicación': record.ubicacion,
          'Observación': record.observacion || '',
          'Fecha Alta': record.fecha_alta,
          'Fecha Cierre': record.fecha_cierre || '',
          'Estado': record.estado
        }))
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Silos Bolsas');
      XLSX.writeFile(workbook, `silos_bolsas_${new Date().toISOString().split('T')[0]}.xlsx`);
      messageApi.success('Archivo Excel exportado correctamente');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      messageApi.error('Error al exportar el archivo Excel');
    }
  };

  // Función para exportar a PDF
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text('Listado de Silos Bolsas', 14, 15);
      doc.setFontSize(10);
      doc.text(`Fecha: ${new Date().toLocaleDateString('es-AR')}`, 14, 22);
      doc.text(`Total de registros: ${filteredData.length}`, 14, 28);

      const tableData = filteredData.map(record => [
        record.numero_silobolsa.toString(),
        record.kilos.toLocaleString('es-AR'),
        record.ubicacion.substring(0, 20),
        record.estado,
        record.fecha_alta
      ]);

      (doc as any).autoTable({
        head: [['N° Silo', 'Kilos', 'Ubicación', 'Estado', 'Fecha Alta']],
        body: tableData,
        startY: 35,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [45, 92, 146] }
      });

      doc.save(`silos_bolsas_${new Date().toISOString().split('T')[0]}.pdf`);
      messageApi.success('Archivo PDF exportado correctamente');
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      messageApi.error('Error al exportar el archivo PDF');
    }
  };


  return (
    <App>
      <div style={{ padding: '0' }}>
        {/* Título y estadísticas compactas */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h1 style={{ 
            color: theme.colors.primaryDark, 
            fontSize: '28px', 
            fontWeight: 600, 
            margin: 0
          }}>
            Silos Bolsas
          </h1>

          {/* Estadísticas compactas en la barra superior */}
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              backgroundColor: theme.colors.backgroundGray,
              borderRadius: '6px',
              border: `1px solid ${theme.colors.borderLight}`
            }}>
              <DatabaseOutlined style={{ fontSize: '16px', color: theme.colors.primary }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>Total:</Text>
              <Text strong style={{ fontSize: '14px', color: theme.colors.textPrimary }}>
                {estadisticas.total}
              </Text>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              backgroundColor: theme.colors.successLight,
              borderRadius: '6px',
              border: `1px solid ${theme.colors.success}`
            }}>
              <CheckCircleOutlined style={{ fontSize: '16px', color: theme.colors.successDark }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>Activos:</Text>
              <Text strong style={{ fontSize: '14px', color: theme.colors.successDark }}>
                {estadisticas.activos}
              </Text>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              backgroundColor: theme.colors.warningLight,
              borderRadius: '6px',
              border: `1px solid ${theme.colors.warning}`
            }}>
              <WarningOutlined style={{ fontSize: '16px', color: theme.colors.warningDark }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>Cerrados:</Text>
              <Text strong style={{ fontSize: '14px', color: theme.colors.warningDark }}>
                {estadisticas.cerrados}
              </Text>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              backgroundColor: theme.colors.infoLight,
              borderRadius: '6px',
              border: `1px solid ${theme.colors.info}`
            }}>
              <DropboxOutlined style={{ fontSize: '16px', color: theme.colors.infoDark }} />
              <Text type="secondary" style={{ fontSize: '12px' }}>Kilos:</Text>
              <Text strong style={{ fontSize: '14px', color: theme.colors.infoDark }}>
                {estadisticas.kilosTotal.toLocaleString('es-AR')}
              </Text>
            </div>

            <Button
              type="text"
              size="small"
              icon={showStatsExpanded ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setShowStatsExpanded(!showStatsExpanded)}
              style={{
                fontSize: '12px',
                color: theme.colors.textSecondary
              }}
            >
              {showStatsExpanded ? 'Ocultar detalles' : 'Ver más'}
            </Button>
          </div>
        </div>

        {/* Cards de resumen expandidas (colapsables) */}
        {showStatsExpanded && (
          <div style={{ 
            display: 'grid', 
            gap: '16px', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            marginBottom: '24px',
            animation: 'fadeIn 0.3s ease-in'
          }}>
            <Card 
              style={{ 
                backgroundColor: '#fff',
                borderRadius: theme.styles.borderRadius,
                boxShadow: theme.styles.boxShadowLight,
                borderTop: `4px solid ${theme.colors.primary}`
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Total Silos</Text>
                  <Title level={3} style={{ margin: '8px 0 0 0', color: theme.colors.textPrimary }}>
                    {estadisticas.total}
                  </Title>
                </div>
                <DatabaseOutlined style={{ fontSize: '32px', color: theme.colors.primary }} />
              </div>
            </Card>

            <Card 
              style={{ 
                backgroundColor: '#fff',
                borderRadius: theme.styles.borderRadius,
                boxShadow: theme.styles.boxShadowLight,
                borderTop: `4px solid ${theme.colors.success}`
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Activos</Text>
                  <Title level={3} style={{ margin: '8px 0 0 0', color: theme.colors.successDark }}>
                    {estadisticas.activos}
                  </Title>
                </div>
                <CheckCircleOutlined style={{ fontSize: '32px', color: theme.colors.success }} />
              </div>
            </Card>

            <Card
              style={{
                backgroundColor: '#fff',
                borderRadius: theme.styles.borderRadius,
                boxShadow: theme.styles.boxShadowLight,
                borderTop: `4px solid ${theme.colors.warning}`
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Cerrados</Text>
                  <Title level={3} style={{ margin: '8px 0 0 0', color: theme.colors.warningDark }}>
                    {estadisticas.cerrados}
                  </Title>
                </div>
                <WarningOutlined style={{ fontSize: '32px', color: theme.colors.warning }} />
              </div>
            </Card>

            <Card
              style={{
                backgroundColor: '#fff',
                borderRadius: theme.styles.borderRadius,
                boxShadow: theme.styles.boxShadowLight,
                borderTop: `4px solid ${theme.colors.error}`
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Baja</Text>
                  <Title level={3} style={{ margin: '8px 0 0 0', color: theme.colors.errorDark }}>
                    {estadisticas.baja}
                  </Title>
                </div>
                <CloseCircleOutlined style={{ fontSize: '32px', color: theme.colors.error }} />
              </div>
            </Card>

            <Card
              style={{
                backgroundColor: '#fff',
                borderRadius: theme.styles.borderRadius,
                boxShadow: theme.styles.boxShadowLight,
                borderTop: `4px solid ${theme.colors.info}`
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Kilos Totales</Text>
                  <Title level={3} style={{ margin: '8px 0 0 0', color: theme.colors.infoDark }}>
                    {estadisticas.kilosTotal.toLocaleString('es-AR')}
                  </Title>
                  <Text style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                    Promedio: {estadisticas.promedioKilos.toLocaleString('es-AR')} kg
                  </Text>
                </div>
                <DropboxOutlined style={{ fontSize: '32px', color: theme.colors.info }} />
              </div>
            </Card>

            {estadisticas.topUbicaciones.length > 0 && (
              <Card 
                style={{ 
                  backgroundColor: '#fff',
                  borderRadius: theme.styles.borderRadius,
                  boxShadow: theme.styles.boxShadowLight,
                  borderTop: `4px solid ${theme.colors.secondary}`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                      Top Ubicación
                    </Text>
                    <Title level={5} style={{ margin: '8px 0 0 0', color: theme.colors.textPrimary }}>
                      {estadisticas.topUbicaciones[0]?.nombre || 'N/A'}
                    </Title>
                    <Text style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                      {estadisticas.topUbicaciones[0]?.count || 0} silos
                    </Text>
                  </div>
                  <CalendarOutlined style={{ fontSize: '32px', color: theme.colors.secondary }} />
                </div>
              </Card>
            )}
          </div>
        )}

      <StandardCard
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DropboxOutlined />
            Listado de silos bolsas
          </span>
        }
        extra={
          <Space>
            <ColumnManagerDropdown
              manager={columnManager}
              labels={columnNames}
              requiredKeys={['acciones']}
              buttonSize="small"
            />
            <ExportExcelButton
              onClick={handleExportExcel}
              size="small"
              disabled={filteredData.length === 0}
            />
            <ExportPDFButton
              onClick={handleExportPDF}
              size="small"
              disabled={filteredData.length === 0}
            />
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleNuevo} 
              size="small"
              style={{
                backgroundColor: theme.colors.secondary,
                borderColor: theme.colors.secondary,
                fontWeight: 500
              }}
            >
              Nuevo
            </Button>
          </Space>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Barra de búsqueda global */}
          <div style={{ marginBottom: '16px' }}>
            <Input
              placeholder="Buscar en todos los campos..."
              prefix={<SearchOutlined style={{ color: theme.colors.textSecondary }} />}
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              allowClear
              style={{
                maxWidth: '400px',
                width: '100%'
              }}
              size="large"
            />
          </div>

          {/* Mensaje de resultados filtrados */}
          {globalSearch && (
            <div style={{ 
              marginBottom: '12px', 
              padding: '8px 12px', 
              backgroundColor: theme.colors.backgroundGray,
              borderRadius: theme.styles.borderRadius,
              fontSize: '14px',
              color: theme.colors.textSecondary
            }}>
              Mostrando {filteredData.length} de {silosBolsas.length} silos bolsas
              {globalSearch && ` que coinciden con "${globalSearch}"`}
            </div>
          )}

          <Table
            columns={filteredColumns.map(col => {
              // Aplicar highlighting a las columnas de texto si hay búsqueda global
              if (globalSearch && col.key) {
                const key = col.key as string;
                if (key === 'numero_silobolsa') {
                  return {
                    ...col,
                    render: (value: number) => {
                      const highlighted = highlightText(value.toString(), globalSearch);
                      return (
                        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
                          {highlighted}
                        </span>
                      );
                    }
                  };
                }
                if (key === 'ubicacion' || key === 'observacion') {
                  return {
                    ...col,
                    render: (text: string) => {
                      const highlighted = highlightText(String(text || ''), globalSearch);
                      return (
                        <span style={{ color: theme.colors.textSecondary }}>
                          {highlighted}
                        </span>
                      );
                    }
                  };
                }
                if (key === 'estado') {
                  return {
                    ...col,
                    render: (estado: string) => {
                      const isActivo = estado === 'Activo';
                      const isCerrado = estado === 'Cerrado';
                      const backgroundColor = isActivo ? theme.colors.successLight : isCerrado ? theme.colors.warningLight : theme.colors.errorLight;
                      const color = isActivo ? theme.colors.successDark : isCerrado ? theme.colors.warningDark : theme.colors.errorDark;
                      const highlighted = highlightText(estado, globalSearch);
                      
                      return (
                        <span style={{ 
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '9999px',
                          fontSize: '12px',
                          fontWeight: 600,
                          backgroundColor,
                          color
                        }}>
                          {highlighted}
                        </span>
                      );
                    }
                  };
                }
              }
              return col;
            })}
            dataSource={filteredData}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} silos bolsas`,
              position: ['bottomRight']
            }}
            scroll={{ x: 1200, y: 'max(150px, calc(100vh - 400px))' }}
            size="middle"
            onRow={(record) => ({
              onMouseEnter: (e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#F5F8FB';
              },
              onMouseLeave: (e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '';
              },
              style: {
                transition: 'background-color 0.2s ease'
              }
            })}
          />
        </div>
      </StandardCard>
    </div>
    </App>
  );
};

export default ListadoSilobolsa;
